import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
  Modal,
  TouchableWithoutFeedback,
  ScrollView,
  Dimensions,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography, chat } from '../../theme';
import { useAppContext } from '../../contexts/AppContext';
import { apiService } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { formatNumber } from '../../utils/rtl';
import { createTranslator } from '../../i18n';
import { useThemedRTLStyles } from '../../hooks/useThemedRTLStyles';

/** Action taken in this message: placed (invoice sent), confirmed (user/business confirmed), cancelled */
export type OrderMessageAction = 'placed' | 'confirmed' | 'cancelled';

export interface OrderMessageCardProps {
  orderId: string;
  action: OrderMessageAction;
  isOutgoing?: boolean;
  specialCard?: boolean;
  contactDisplayName?: string;
  conversationId?: string;
}

const ACTION_ICONS: Record<OrderMessageAction, { icon: keyof typeof Ionicons.glyphMap; color: string }> = {
  placed: { icon: 'receipt-outline', color: colors.primary },
  confirmed: { icon: 'checkmark-circle-outline', color: '#0ea56a' },
  cancelled: { icon: 'close-circle-outline', color: '#b91c1c' },
};

const ORDER_STATUS_VALUES = ['pending', 'placed', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'] as const;
const PAYMENT_STATUS_VALUES = ['unpaid', 'partial', 'paid', 'refunded'] as const;

const INBOUND_ACTION_ICONS: Record<'confirmed' | 'cancelled', { icon: keyof typeof Ionicons.glyphMap; color: string }> = {
  confirmed: { icon: 'checkmark-circle', color: '#0ea56a' },
  cancelled: { icon: 'close-circle', color: '#b91c1c' },
};

const { width: screenWidth } = Dimensions.get('window');
const isNative = Platform.OS !== 'web';
// Responsive: on native or narrow screens avoid fixed min widths so cards don't overflow
const orderCardMaxWidth = isNative ? screenWidth * 0.6 : undefined;
const inlineStatusMinWidth = isNative || screenWidth < 400 ? 0 : 280;
const inlineStatusMaxWidth = isNative || screenWidth < 400 ? screenWidth - 48 : 340;

export const OrderMessageCard: React.FC<OrderMessageCardProps> = ({
  orderId,
  action,
  isOutgoing = false,
  specialCard = false,
  contactDisplayName,
  conversationId,
}) => {
  const styles = useThemedRTLStyles(getStyles);
  const { navigateTo, showNotification } = useAppContext();
  const { locale } = useLocale();
  const t = createTranslator(locale);
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);

  const isInboundStatusUpdate = specialCard && !isOutgoing && (action === 'confirmed' || action === 'cancelled');
  const isBusinessCard = isOutgoing;

  const actionConfig = {
    placed: { label: t('card.orderPlaced'), subLabel: t('card.orderPlacedDesc'), ...ACTION_ICONS.placed },
    confirmed: { label: t('card.orderConfirmed'), subLabel: t('card.orderConfirmedDesc'), ...ACTION_ICONS.confirmed },
    cancelled: { label: t('card.orderCancelledTitle'), subLabel: t('card.orderCancelledDesc'), ...ACTION_ICONS.cancelled },
  };
  const orderStatusLabels: Record<string, string> = {
    pending: t('card.statusPending'),
    placed: t('card.statusPlaced'),
    confirmed: t('card.statusConfirmedOrder'),
    processing: t('card.statusProcessing'),
    shipped: t('card.statusShipped'),
    delivered: t('card.statusDelivered'),
    cancelled: t('card.statusCancelledOrder'),
  };
  const paymentStatusLabels: Record<string, string> = {
    unpaid: t('card.paymentUnpaid'),
    partial: t('card.paymentPartial'),
    paid: t('card.paymentPaid'),
    refunded: t('card.paymentRefunded'),
  };
  const orderStatusOptions = ORDER_STATUS_VALUES.map((value) => ({ value, label: orderStatusLabels[value] || value }));
  const paymentStatusOptions = PAYMENT_STATUS_VALUES.map((value) => ({ value, label: paymentStatusLabels[value] || value }));
  const inboundActionConfig = {
    confirmed: INBOUND_ACTION_ICONS.confirmed,
    cancelled: INBOUND_ACTION_ICONS.cancelled,
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }
      try {
        const res = await apiService.getOrderById(orderId);
        if (!cancelled && res?.data) setOrder(res.data);
      } catch (_) {
        if (!cancelled) setOrder(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true };
  }, [orderId]);

  const handleViewOrder = () => navigateTo('order-edit', orderId);

  const handleCopyOrderNumber = async () => {
    const num = order?.orderNumber || orderId;
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(num);
      } else {
        await Clipboard.setStringAsync(num);
      }
      showNotification('success', t('card.orderCopied'), t('card.orderCopiedDesc'));
    } catch (_) {
      showNotification('error', t('card.orderCopyFailed'), t('card.orderCopyFailedDesc'));
    }
  };

  const handleShowNotes = () => {
    if (order?.notes) {
      if (Platform.OS === 'web') {
        window.alert(order.notes);
      } else {
        Alert.alert(t('card.orderNotes'), order.notes, [{ text: t('card.ok') }]);
      }
    }
  };

  const notifyContactOfStatusChange = async () => {
    if (!conversationId || !orderId) return;
    try {
      await apiService.sendConversationMessage(conversationId, {
        type: 'order_status',
        orderId,
        content: '', // Backend builds formatted message from order + quick reply config
      });
    } catch (e) {
      console.warn('[OrderMessageCard] Failed to notify contact:', e);
    }
  };

  const updateOrderStatus = async (newStatus: string) => {
    if (!orderId) return;
    setStatusModalVisible(false);
    setUpdating(true);
    try {
      await apiService.updateOrder(orderId, { orderStatus: newStatus as any });
      const res = await apiService.getOrderById(orderId);
      if (res?.data) setOrder(res.data);
      showNotification('success', t('card.statusUpdated'), t('card.orderStatusUpdated', { status: newStatus }));
      await notifyContactOfStatusChange();
    } catch (e: any) {
      showNotification('error', t('card.updateFailed'), e?.message || t('card.updateOrderFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const updatePaymentStatus = async (newPaymentStatus: string) => {
    if (!orderId) return;
    setPaymentModalVisible(false);
    setUpdating(true);
    try {
      await apiService.updateOrder(orderId, { paymentStatus: newPaymentStatus as any });
      const res = await apiService.getOrderById(orderId);
      if (res?.data) setOrder(res.data);
      showNotification('success', t('card.paymentUpdated'), t('card.paymentStatusUpdated', { status: newPaymentStatus }));
      await notifyContactOfStatusChange();
    } catch (e: any) {
      showNotification('error', t('card.updateFailed'), e?.message || t('card.updatePaymentFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const config = actionConfig[action];
  const cardStyles = specialCard ? styles.specialCard : (isOutgoing ? styles.cardOutgoing : styles.cardIncoming);
  const textStyles = specialCard ? styles.specialText : (isOutgoing ? styles.textOutgoing : styles.textIncoming);

  if (loading) {
    return (
      <View style={[styles.card, cardStyles]}>
        <View style={styles.headerRow}>
          <Ionicons name="receipt-outline" size={20} color={specialCard ? colors.primary : (isOutgoing ? chat.out.accent : colors.primary)} />
        </View>
        <ActivityIndicator size="small" color={specialCard ? colors.primary : (isOutgoing ? chat.out.accent : colors.primary)} style={{ padding: spacing.lg }} />
      </View>
    );
  }

  if (isInboundStatusUpdate) {
    const inboundConfig = inboundActionConfig[action as 'confirmed' | 'cancelled'];
    const name = (contactDisplayName || t('card.customer')).trim() || t('card.customer');
    const statusColor = inboundConfig.color;
    const fullText = action === 'confirmed'
      ? t('card.confirmedOrder', { name })
      : t('card.cancelledOrder', { name });
    return (
      <View style={styles.inlineStatusContainer}>
        <View style={styles.inlineStatusRow}>
          <Ionicons name={inboundConfig.icon as any} size={24} color={statusColor} style={styles.inlineStatusIcon} />
          <Text style={[styles.inlineStatusText, { color: statusColor }]} numberOfLines={2}>
            {fullText}
          </Text>
        </View>
      </View>
    );
  }

  const orderNumber = order?.orderNumber || orderId;
  const rawStatus = order?.orderStatus;
  const status = typeof rawStatus === 'string' ? rawStatus : (rawStatus && typeof rawStatus === 'object' && (rawStatus as any).status != null ? String((rawStatus as any).status) : '—');
  const rawPayment = order?.paymentStatus;
  const paymentStatus = typeof rawPayment === 'string' ? rawPayment : (rawPayment && typeof rawPayment === 'object' && (rawPayment as any).status != null ? String((rawPayment as any).status) : null);
  const paymentStatusStr = paymentStatus ?? '—';
  const total = order?.totalPrice != null ? `${order.currency || ''} ${formatNumber(Number(order.totalPrice), String(locale || 'en').split(/[_-]/)[0], { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : null;
  const createdDate =
    order?.orderDate || order?.createdAt
      ? new Date(order.orderDate || order.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })
      : null;
  const hasNotes = !!(order?.notes && String(order.notes).trim());

  return (
    <>
      <View style={[styles.card, cardStyles]}>
        {/* Header: icon + #orderNumber + copy + view */}
        <View style={[styles.headerRow, specialCard && styles.headerRowSpecial]}>
          <View style={styles.headerOrderRow}>
            <Ionicons name="receipt-outline" size={20} color={specialCard ? colors.primary : (isOutgoing ? chat.out.accent : colors.primary)} style={styles.headerOrderIcon} />
            <Text style={[styles.orderNumberText, textStyles]} numberOfLines={1}>
              #{orderNumber}
            </Text>
          </View>
          <View style={styles.headerIcons}>
            <TouchableOpacity onPress={handleCopyOrderNumber} style={styles.iconBtn} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="copy-outline" size={18} color={specialCard ? colors.text.primary : (isOutgoing ? chat.out.accent : colors.primary)} />
            </TouchableOpacity>
            <TouchableOpacity onPress={handleViewOrder} style={styles.iconBtn} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="eye-outline" size={20} color={specialCard ? colors.primary : (isOutgoing ? chat.out.accent : colors.primary)} />
            </TouchableOpacity>
          </View>
        </View>

        {/* Body: action label, date, status, payment, total, notes */}
        <View style={[styles.body, specialCard && styles.bodySpecial]}>
          {specialCard && (
            <View style={[styles.actionBadge, { backgroundColor: (config as any).color + '18' }]}>
              <Ionicons name={(config as any).icon as any} size={14} color={(config as any).color} />
              <Text style={[styles.actionBadgeText, { color: (config as any).color }]}>{(config as any).label}</Text>
            </View>
          )}
          {/* Single line: date · status · payment · total — no labels */}
          <View style={styles.metaLine}>
            {createdDate && (
              <Text style={[styles.metaMuted, textStyles]}>{createdDate}</Text>
            )}
            {isBusinessCard ? (
              <>
                <TouchableOpacity
                  onPress={() => setStatusModalVisible(true)}
                  disabled={updating}
                  style={styles.chipTouchable}
                >
                  <View style={[styles.chip, specialCard ? styles.chipSpecial : (isOutgoing ? styles.chipOutgoing : styles.chipIncoming)]}>
                    {updating ? (
                      <ActivityIndicator size="small" color={specialCard ? colors.primary : (isOutgoing ? chat.out.accent : colors.primary)} />
                    ) : (
                      <>
                        <Text style={[styles.chipText, textStyles]}>{status}</Text>
                        <Ionicons name="chevron-down" size={12} color={specialCard ? colors.text.secondary : (isOutgoing ? chat.out.subtle : colors.text.secondary)} />
                      </>
                    )}
                  </View>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => setPaymentModalVisible(true)}
                  disabled={updating}
                  style={styles.chipTouchable}
                >
                  <View style={[styles.chip, specialCard ? styles.chipSpecial : (isOutgoing ? styles.chipOutgoing : styles.chipIncoming)]}>
                    <Text style={[styles.chipText, textStyles]}>{paymentStatusStr}</Text>
                    {!updating && <Ionicons name="chevron-down" size={12} color={specialCard ? colors.text.secondary : (isOutgoing ? chat.out.subtle : colors.text.secondary)} />}
                  </View>
                </TouchableOpacity>
              </>
            ) : (
              <>
                <View style={[styles.chip, styles.chipStatic, specialCard ? styles.chipSpecial : (isOutgoing ? styles.chipOutgoing : styles.chipIncoming)]}>
                  <Text style={[styles.chipText, textStyles]}>{status}</Text>
                </View>
                {(paymentStatusStr != null && paymentStatusStr !== '—' && paymentStatusStr !== '') && (
                  <View style={[styles.chip, styles.chipStatic, specialCard ? styles.chipSpecial : (isOutgoing ? styles.chipOutgoing : styles.chipIncoming)]}>
                    <Text style={[styles.chipText, textStyles]}>{paymentStatusStr}</Text>
                  </View>
                )}
              </>
            )}
            {total ? (
              <>
                <View style={styles.metaSpacer} />
                <Text style={[styles.metaTotal, textStyles]}>{total}</Text>
              </>
            ) : null}
          </View>

          {hasNotes && (
            <TouchableOpacity onPress={handleShowNotes} style={styles.notesRow}>
              <Ionicons name="document-text-outline" size={18} color={specialCard ? colors.primary : (isOutgoing ? chat.out.accent : colors.primary)} />
              <Text style={[styles.notesLabel, textStyles]}>{t('card.orderNotes')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Status picker modal — no title, close only */}
      <Modal visible={statusModalVisible} transparent animationType="fade" onRequestClose={() => setStatusModalVisible(false)}>
        <TouchableWithoutFeedback onPress={() => setStatusModalVisible(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalBox}>
                <View style={styles.modalHeaderMinimal}>
                  <TouchableOpacity onPress={() => setStatusModalVisible(false)} hitSlop={12}>
                    <Ionicons name="close" size={24} color={colors.text.primary} />
                  </TouchableOpacity>
                </View>
                <ScrollView style={styles.modalScroll}>
                  {orderStatusOptions.map((opt) => (
                    <TouchableOpacity
                      key={opt.value}
                      style={[styles.modalOption, status === opt.value && styles.modalOptionActive]}
                      onPress={() => updateOrderStatus(opt.value)}
                    >
                      <Text style={[styles.modalOptionText, { color: status === opt.value ? colors.primary : colors.text.primary }]}>
                        {opt.label}
                      </Text>
                      {status === opt.value && <Ionicons name="checkmark" size={20} color={colors.primary} />}
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Payment picker modal — no title, close only */}
      <Modal visible={paymentModalVisible} transparent animationType="fade" onRequestClose={() => setPaymentModalVisible(false)}>
        <TouchableWithoutFeedback onPress={() => setPaymentModalVisible(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalBox}>
                <View style={styles.modalHeaderMinimal}>
                  <TouchableOpacity onPress={() => setPaymentModalVisible(false)} hitSlop={12}>
                    <Ionicons name="close" size={24} color={colors.text.primary} />
                  </TouchableOpacity>
                </View>
                <ScrollView style={styles.modalScroll}>
                  {paymentStatusOptions.map((opt) => (
                    <TouchableOpacity
                      key={opt.value}
                      style={[styles.modalOption, paymentStatus === opt.value && styles.modalOptionActive]}
                      onPress={() => updatePaymentStatus(opt.value)}
                    >
                      <Text style={[styles.modalOptionText, { color: paymentStatus === opt.value ? colors.primary : colors.text.primary }]}>
                        {opt.label}
                      </Text>
                      {paymentStatus === opt.value && <Ionicons name="checkmark" size={20} color={colors.primary} />}
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    borderRadius: 20,
    padding: 14,
    width: orderCardMaxWidth,
    maxWidth: orderCardMaxWidth ?? '92%',
    alignSelf: 'stretch',
    minWidth: 0,
  },
  cardOutgoing: {
    backgroundColor: chat.out.bg,
  },
  cardIncoming: {
    backgroundColor: colors.background.secondary,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  headerRowSpecial: {
    marginBottom: spacing.xs,
  },
  headerOrderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  headerOrderIcon: {
    marginRight: spacing.xs,
  },
  orderNumberText: {
    flex: 1,
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    marginRight: spacing.sm,
  },
  headerIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  iconBtn: {
    padding: spacing.xs,
  },
  body: {
    marginTop: 0,
  },
  bodySpecial: {
    paddingTop: 0,
  },
  actionBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 8,
    marginBottom: spacing.sm,
  },
  actionBadgeText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
  },
  metaLine: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  metaMuted: {
    fontSize: typography.fontSize.xs,
    opacity: 0.85,
  },
  chipTouchable: {},
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 8,
  },
  chipStatic: {},
  chipSpecial: {
    backgroundColor: colors.background.secondary,
  },
  chipOutgoing: {
    backgroundColor: chat.out.chip,
  },
  chipIncoming: {
    backgroundColor: colors.border,
  },
  chipText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
  metaSpacer: {
    flex: 1,
    minWidth: 8,
  },
  metaTotal: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
  },
  textOutgoing: {
    color: chat.out.text,
  },
  textIncoming: {
    color: colors.text.primary,
  },
  notesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  notesLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  specialCard: {
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    borderRadius: 14,
    padding: spacing.md,
    width: orderCardMaxWidth,
    maxWidth: orderCardMaxWidth ?? '100%',
    alignSelf: 'stretch',
    minWidth: 0,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 4,
  },
  specialText: {
    color: colors.text.primary,
  },
  inlineStatusContainer: {
    width: '100%',
    minWidth: inlineStatusMinWidth,
    maxWidth: inlineStatusMaxWidth,
    alignSelf: 'stretch',
  },
  inlineStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  inlineStatusIcon: {
    marginRight: spacing.sm,
  },
  inlineStatusText: {
    flex: 1,
    minWidth: 0,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
  },
  inlineStatusName: {
    fontFamily: typography.fontFamily.semibold,
  },
  inlineStatusVerb: {},
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  modalBox: {
    backgroundColor: colors.background.surface,
    borderRadius: 12,
    maxWidth: 400,
    width: '100%',
    maxHeight: '80%',
  },
  modalHeaderMinimal: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.sm,
    paddingBottom: 0,
  },
  modalScroll: {
    maxHeight: 320,
    padding: spacing.sm,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    marginBottom: 2,
  },
  modalOptionActive: {
    backgroundColor: colors.primaryTransparent,
  },
  modalOptionText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
});
